import React from "react";

import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import { AffiliateCheckbox } from "@app/components/landing-pages/affiliate-checkbox";
import type { SearchFormStyleVariant } from "@app/components/search-form/search-form";
import { SearchFormContainer } from "@app/components/search-form/search-form-container";
import { openModal } from "@app/components/search-form-hydrated/autocomplete-input-hydrated/hooks/use-modal-state";
import {
  getLegacySearchStateValues,
  useAffiliateOptin,
  useDefaultSearchLocationValues,
  useLegacySearchFormPassengers,
  useSearchLocationValues
} from "@app/components/search-form-hydrated/autocomplete-input-hydrated/hooks/use-search-state-hooks";
import {
  ancillaryRedirectAffiliateAndShowBusbudResults,
  redirectToResultsPage,
  redirectWebComponentToResultsPage
} from "@app/components/search-form-hydrated/autocomplete-input-hydrated/search-redirect-helpers";
import type { CompactConfiguration } from "@app/components/search-form-hydrated/search-form-date-passenger-submit-section";
import { SearchFormDatePassengerSubmitSection } from "@app/components/search-form-hydrated/search-form-date-passenger-submit-section";
import { SearchFormDevTools } from "@app/components/search-form-hydrated/search-form-devtools";
import { SearchFormOriginDestinationSection } from "@app/components/search-form-hydrated/search-form-origin-destination-section";
import { shouldIncreaseReturnDate } from "@app/components/search-form-hydrated/search-form.helpers";
import { AffiliatePartners } from "@app/constants/affiliate-partners";
import { useWhitelabelContext } from "@app/context/whitelabel-context";
import { useLiteAppContext } from "@app/helpers/hooks";
import { LocalStorage } from "@app/lib/bowser-storage/local-storage";
import { SEARCH_ERRORS } from "@app/modules/search/constants";
import { saveRecentSearch } from "@app/modules/search/helpers/recent-searches";
import {
  getResultsPageUrl,
  getSearchQuery
} from "@app/modules/search/helpers/submit";
import { useDateInputs } from "@app/modules/search/store/hooks/date-inputs";
import { usePassengers } from "@app/modules/search/store/hooks/passengers";
import { isMissingAges } from "@app/modules/search/store/selectors/passengers";
import {
  setAffiliateOptin,
  setOutboundDate,
  setReturnDate
} from "@app/modules/search/store/slices/search-form";
import {
  clickedAffiliateCheckbox,
  failedRouteSearch,
  searchedRoute,
  selectedDepartureDate,
  selectedReturnDate,
  unselectedReturnDate
} from "@app/tracking/search-tracking";
import type { SearchFormMergedState } from "@app/types/search-types";
import {
  addDaysFormatted,
  getTomorrowDateFormatted
} from "@app/utils/dates-without-moment";
import { yieldToMain } from "@app/utils/scheduler-yield";
import { appendQueryToCurrentLocation } from "@app/utils/window";

import { isSupportedLocale } from "@config/locales";

import type { SubmitHandler } from "react-hook-form";

import { search_form_widget_state } from "./autocomplete-input-hydrated/hooks/use-search-widget-state";

export interface SearchFormValues {
  origin: string;
  destination: string;
  outbound_date: string;
  return_date: string;
}

export interface Props {
  compact_configuration: CompactConfiguration;
  search_label: string;
  one_way_only?: boolean;
  with_affiliate_checkbox?: boolean;
  style_variant?: SearchFormStyleVariant;
  className?: string;
  profile_ids?: number[];
}

export const SearchFormHydrated: React.FC<Props> = ({
  compact_configuration,
  search_label,
  style_variant,
  with_affiliate_checkbox = false,
  one_way_only = false,
  profile_ids,
  ...props
}) => {
  const { features, tracker, locale, user_country_code } = useLiteAppContext();
  const dispatch = useDispatch();
  const { whitelabel: dynamic_whitelabel } = useWhitelabelContext();
  const isMountedRef = React.useRef(false);
  const { return_date, outbound_date } = useDateInputs();

  const { passengers } = usePassengers();

  const legacy_passenger_state = useLegacySearchFormPassengers();

  const { origin, destination } = useDefaultSearchLocationValues();
  const initial_locations = React.useRef({
    initial_origin: origin.value,
    initial_destination: destination.value
  });
  const { recent_searches } = useSearchLocationValues();
  const affiliate_optin_redux = useAffiliateOptin();

  const { handleSubmit, control, setValue, getValues } =
    useForm<SearchFormValues>({
      defaultValues: {
        origin: origin.formatted,
        destination: destination.formatted,
        outbound_date: outbound_date || "",
        return_date: return_date || ""
      },
      mode: "onSubmit"
    });

  React.useEffect(
    function syncOutboundDateInputValues() {
      const prev_date = getValues("outbound_date");
      let new_outbound_date = outbound_date;

      if (!isMountedRef.current) {
        if (!new_outbound_date) {
          new_outbound_date = getTomorrowDateFormatted();
          dispatch(setOutboundDate(new_outbound_date));
        }
        isMountedRef.current = true;
      }

      if (!new_outbound_date) {
        return;
      }

      setValue("outbound_date", new_outbound_date, {
        shouldDirty: true
      });

      const return_date = getValues("return_date");
      if (shouldIncreaseReturnDate(new_outbound_date, return_date)) {
        dispatch(setReturnDate(addDaysFormatted(new_outbound_date, 1)));
      }

      if (prev_date && prev_date !== new_outbound_date) {
        tracker?.asyncTrack(
          selectedDepartureDate(prev_date, new_outbound_date)
        );
      }
    },
    [outbound_date, setValue, getValues, tracker, dispatch]
  );

  React.useEffect(
    function syncReturnDateInputValues() {
      const prev_date = getValues("return_date");
      setValue("return_date", return_date || "", {
        shouldDirty: true
      });

      if (return_date) {
        tracker?.asyncTrack(selectedReturnDate(prev_date ?? null, return_date));
      } else if (prev_date && !return_date) {
        tracker?.asyncTrack(unselectedReturnDate());
      }
    },
    [return_date, setValue, getValues, tracker]
  );

  // On slow connection, users may end up clicking on the submit button before the search form is hydrated
  // In that case, the default HTML form behaviour kicks in: it will redirect to the URL declared in `action`
  // With the input values of the form as query parameters
  // We decided to go that way to ensure people clicking on the search button on slow connection don't enter
  // an infinite loop of reloads when they click.
  const default_form_action =
    origin.value && destination.value
      ? getResultsPageUrl(
          { locale },
          {
            origin: origin.value,
            destination: destination.value,
            affiliate_optin: affiliate_optin_redux,
            outbound_date,
            return_date,
            vehicle_category: undefined,
            profile_ids,
            locale,
            recent_searches: {
              origin: null,
              destination: null
            },
            ...legacy_passenger_state
          }
        )
      : undefined;

  const onSubmit: SubmitHandler<SearchFormValues> = async data => {
    const { outbound_date, return_date } = data;
    const { whitelabel_context, ...legacy_state_values } =
      getLegacySearchStateValues();
    const { lang, whitelabel, push_state_disabled } = legacy_state_values;

    const state: Omit<SearchFormMergedState, "passengers"> = {
      origin: origin.value,
      destination: destination.value,
      affiliate_optin: affiliate_optin_redux,
      outbound_date,
      return_date,
      vehicle_category: undefined,
      profile_ids,
      ...legacy_passenger_state,
      ...legacy_state_values
    };

    if (!(origin.value && destination.value && outbound_date)) {
      return;
    }

    const search_with_accomodation_affiliate = affiliate_optin_redux
      ? AffiliatePartners.BOOKING_COM
      : "none";

    await yieldToMain();

    if (
      isMissingAges({
        search_form: {
          passengers
        }
      })
    ) {
      openModal("passengers");
      tracker?.asyncTrack(
        failedRouteSearch({
          state: {
            ...state,
            origin: origin.value,
            destination: destination.value,
            outbound_date
          },
          errors: [SEARCH_ERRORS.MISSING_AGE],
          search_with_accomodation_affiliate
        })
      );
      return;
    }

    const results_options = {
      locale: isSupportedLocale(lang) ? lang : ("en" as const),
      features,
      whitelabel:
        whitelabel ||
        whitelabel_context?.whitelabel_name ||
        dynamic_whitelabel?.name,
      state,
      tracker,
      user_country_code
    };

    const browser_storage = LocalStorage.fromWindow(window);

    saveRecentSearch(browser_storage, "origin", recent_searches.origin);
    saveRecentSearch(
      browser_storage,
      "destination",
      recent_searches.destination
    );

    // Tracking
    const { initial_origin, initial_destination } = initial_locations.current;
    tracker?.track(
      searchedRoute({
        state: {
          ...state,
          origin: origin.value,
          destination: destination.value,
          outbound_date
        },
        initial_origin,
        initial_destination,
        search_with_accomodation_affiliate
      })
    );

    // Search form widget
    if (search_form_widget_state.is_web_component) {
      redirectWebComponentToResultsPage(results_options);
      return;
    }

    // Redirect
    if (!push_state_disabled) {
      // This allows us to fill the form when the user clicks back from results page
      appendQueryToCurrentLocation(getSearchQuery(results_options, state));
    }

    if (search_with_accomodation_affiliate !== "none") {
      // Safari popup block: Safari browser will block popups and partners page opening if window.open() is not immediate. async functions are not allowed here
      ancillaryRedirectAffiliateAndShowBusbudResults(
        search_with_accomodation_affiliate,
        results_options
      );
      return;
    }

    redirectToResultsPage(results_options);
  };

  return (
    <>
      <SearchFormContainer
        data-testid="search-form-hydrated"
        onSubmit={handleSubmit(onSubmit)}
        style_variant={style_variant}
        action={default_form_action}
        rel="nofollow"
        {...props}
      >
        {
          // when the form is not hydrated yet, and a user clicks on search
          // they will be redirected to `default_form_action` which is the search results URL
          // but the query params will be overwritten by the inputs in the form
          // Since `adults` is mandatory, we have to add this hidden value
          // to ensure `adults=1` is added in the URL
          !!default_form_action && (
            <input type="hidden" name="adults" value={1} />
          )
        }
        <SearchFormOriginDestinationSection
          control={control}
          setValue={setValue}
          getValues={getValues}
          one_way_only={one_way_only}
        />
        <SearchFormDatePassengerSubmitSection
          search_label={search_label}
          compact_configuration={compact_configuration}
          one_way_only={one_way_only}
          control={control}
        />
      </SearchFormContainer>
      {!!with_affiliate_checkbox && (
        <AffiliateCheckbox
          className="mt-150"
          name="affiliate_optin"
          isChecked={affiliate_optin_redux}
          onChange={e => {
            const updated_value: boolean = !!(e?.currentTarget).checked;
            dispatch(setAffiliateOptin(updated_value));
            tracker?.asyncTrack(
              clickedAffiliateCheckbox(
                AffiliatePartners.BOOKING_COM,
                updated_value
              )
            );
          }}
        />
      )}
      <SearchFormDevTools control={control} />
    </>
  );
};
